import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import type { Card as StudyCard, Deck, UserPreferences } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button, FlagDeckButton } from '../../components'
import { showToast } from '../../components/Toast'
import { getDeck, listCards } from '../../lib/decks.js'
import { isOnline, listenToOnlineStatus } from '../../lib/offline.js'
import { DEFAULT_PREFERENCES, listenToPreferences } from '../../lib/preferences.js'
import { createScore } from '../../lib/scores.js'
import {
  clearSessionSnapshot,
  loadSessionSnapshot,
  saveSessionSnapshot,
  type FlashcardsSessionSnapshot,
} from '../../lib/session-storage.js'
import {
  finishSession,
  markCorrect,
  markMissed,
  startSession,
  type SessionState,
} from '../../lib/study-session.js'
import styles from './FlashcardsPage.module.css'

interface PageState {
  deck: Deck | null
  cards: StudyCard[]
  loading: boolean
  errorMessage: string | null
}

const INITIAL_PAGE_STATE: PageState = {
  deck: null,
  cards: [],
  loading: true,
  errorMessage: null,
}

function starredKey(uid: string, deckId: string) {
  return `flashcards.starred.${uid}.${deckId}`
}

function loadStarred(uid: string, deckId: string) {
  try {
    const raw = window.localStorage.getItem(starredKey(uid, deckId))
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set<string>()
  }
}

function saveStarred(uid: string, deckId: string, starred: Set<string>) {
  window.localStorage.setItem(starredKey(uid, deckId), JSON.stringify(Array.from(starred)))
}

function voiceKey(voice: SpeechSynthesisVoice) {
  return `${voice.name} (${voice.lang})`
}

function findVoice(voices: SpeechSynthesisVoice[], key: string | null) {
  if (!key) return null
  return voices.find((voice) => voiceKey(voice) === key || voice.name === key) ?? null
}

function isTypingTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  )
}

function snapshotFromSession(
  state: SessionState,
  currentIndex: number,
  flipped: boolean,
  missed: Set<string>,
): FlashcardsSessionSnapshot {
  return {
    mode: 'flashcards',
    deckId: state.deckId,
    classId: state.classId,
    startedAt: state.startedAt.toISOString(),
    currentIndex,
    flipped,
    missedCardIds: Array.from(missed),
    cardsSeen: Array.from(state.cardsSeen),
    cardsCorrect: Array.from(state.cardsCorrect),
  }
}

function sessionFromSnapshot(snapshot: FlashcardsSessionSnapshot): SessionState {
  return {
    deckId: snapshot.deckId,
    classId: snapshot.classId,
    mode: 'flashcards',
    startedAt: new Date(snapshot.startedAt),
    cardsSeen: new Set(snapshot.cardsSeen),
    cardsCorrect: new Set(snapshot.cardsCorrect),
    missedCardIds: new Set(snapshot.missedCardIds),
  }
}

export default function FlashcardsPage(): JSX.Element {
  const { deckId } = useParams()
  const { uid, firebaseUser } = useAuth()
  const navigate = useNavigate()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [pageState, setPageState] = useState<PageState>(INITIAL_PAGE_STATE)
  const [session, setSession] = useState<SessionState | null>(null)
  const [index, setIndex] = useState(0)
  const [flipped, setFlipped] = useState(false)
  const [starredOnly, setStarredOnly] = useState(false)
  const [starred, setStarred] = useState<Set<string>>(new Set())
  const [missed, setMissed] = useState<Set<string>>(new Set())
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES)
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  const [speaking, setSpeaking] = useState(false)
  const [savingScore, setSavingScore] = useState(false)
  const [scoreError, setScoreError] = useState<string | null>(null)
  const [online, setOnline] = useState(isOnline)

  const loadDeck = useCallback(async () => {
    if (!deckId || !uid) return
    setPageState((previous) => ({ ...previous, loading: true, errorMessage: null }))
    setScoreError(null)

    try {
      const [deck, cards] = await Promise.all([getDeck(deckId), listCards(deckId)])
      setPageState({ deck, cards, loading: false, errorMessage: null })
      const saved = deck && uid ? loadSessionSnapshot(uid, deck.id, 'flashcards') : null
      if (saved?.mode === 'flashcards') {
        setSession(sessionFromSnapshot(saved))
        setIndex(Math.min(saved.currentIndex, Math.max(0, cards.length - 1)))
        setFlipped(saved.flipped)
        setMissed(new Set(saved.missedCardIds))
      } else {
        setSession(deck ? startSession(deck.id, deck.classId) : null)
        setIndex(0)
        setFlipped(false)
        setMissed(new Set())
      }
      setStarred(loadStarred(uid, deckId))
      setStarredOnly(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load this deck.'
      setPageState((previous) => ({ ...previous, loading: false, errorMessage: message }))
      showToast(message, 'error')
    }
  }, [deckId, uid])

  useEffect(() => {
    void loadDeck()
  }, [loadDeck])

  useEffect(() => {
    if (!uid) return
    return listenToPreferences(uid, setPreferences)
  }, [uid])

  useEffect(() => listenToOnlineStatus(setOnline), [])

  useEffect(() => {
    function loadVoices() {
      if (!('speechSynthesis' in window)) return
      setVoices(window.speechSynthesis.getVoices())
    }
    loadVoices()
    if (!('speechSynthesis' in window)) return
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices)
    return () => window.speechSynthesis.removeEventListener('voiceschanged', loadVoices)
  }, [])

  const visibleCards = useMemo(() => {
    if (!starredOnly) return pageState.cards
    return pageState.cards.filter((card) => starred.has(card.id))
  }, [pageState.cards, starred, starredOnly])

  useEffect(() => {
    if (index >= visibleCards.length) {
      setIndex(Math.max(0, visibleCards.length - 1))
    }
  }, [index, visibleCards.length])

  const currentCard = visibleCards[index] ?? null
  const currentText = currentCard ? (flipped ? currentCard.definition : currentCard.term) : ''
  const currentAudioUrl = currentCard
    ? flipped
      ? currentCard.definitionAudioUrl
      : currentCard.termAudioUrl
    : null
  const selectedVoice = useMemo(
    () => findVoice(voices, preferences.ttsVoice),
    [preferences.ttsVoice, voices],
  )
  const hasSpeechVoice = 'speechSynthesis' in window && voices.length > 0
  const canSpeak = Boolean(currentText && (hasSpeechVoice || currentAudioUrl))
  const cardIsMissed = currentCard ? missed.has(currentCard.id) : false
  const cardIsStarred = currentCard ? starred.has(currentCard.id) : false

  useEffect(() => {
    if (!uid || !session || savingScore) return
    saveSessionSnapshot(uid, snapshotFromSession(session, index, flipped, missed))
  }, [flipped, index, missed, savingScore, session, uid])

  function sessionAfterCurrent(baseSession: SessionState, card: StudyCard) {
    return missed.has(card.id)
      ? markMissed(baseSession, card.id)
      : markCorrect(baseSession, card.id)
  }

  async function finishWith(nextSession: SessionState | null = session) {
    if (!uid || !nextSession || savingScore) return
    setSavingScore(true)
    setScoreError(null)
    try {
      await createScore(finishSession(nextSession), uid)
      clearSessionSnapshot(uid, nextSession.deckId, 'flashcards')
      showToast('Saved your session.', 'success')
      navigate('/student')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to save your session.'
      setScoreError(message)
      showToast(message, 'error')
    } finally {
      setSavingScore(false)
    }
  }

  function handlePrevious() {
    if (!currentCard || !session) return
    const nextSession = sessionAfterCurrent(session, currentCard)
    setSession(nextSession)
    setIndex((previous) => Math.max(0, previous - 1))
    setFlipped(false)
  }

  function handleNext() {
    if (!currentCard || !session) return
    const nextSession = sessionAfterCurrent(session, currentCard)
    setSession(nextSession)
    setFlipped(false)
    if (index >= visibleCards.length - 1) {
      void finishWith(nextSession)
      return
    }
    setIndex((previous) => previous + 1)
  }

  function handleFinish() {
    if (!currentCard || !session) {
      void finishWith(session)
      return
    }
    const nextSession = sessionAfterCurrent(session, currentCard)
    setSession(nextSession)
    void finishWith(nextSession)
  }

  function toggleStar() {
    if (!currentCard || !uid || !deckId) return
    setStarred((previous) => {
      const next = new Set(previous)
      if (next.has(currentCard.id)) {
        next.delete(currentCard.id)
      } else {
        next.add(currentCard.id)
      }
      saveStarred(uid, deckId, next)
      return next
    })
  }

  function toggleMissed() {
    if (!currentCard) return
    setMissed((previous) => {
      const next = new Set(previous)
      if (next.has(currentCard.id)) {
        next.delete(currentCard.id)
      } else {
        next.add(currentCard.id)
      }
      return next
    })
  }

  function speak() {
    if (!currentText || !currentCard) return
    if (hasSpeechVoice) {
      window.speechSynthesis.cancel()
      const utterance = new SpeechSynthesisUtterance(currentText)
      utterance.rate = preferences.ttsRate
      if (selectedVoice) utterance.voice = selectedVoice
      utterance.onend = () => setSpeaking(false)
      utterance.onerror = () => setSpeaking(false)
      setSpeaking(true)
      window.speechSynthesis.speak(utterance)
      return
    }
    if (currentAudioUrl && audioRef.current) {
      audioRef.current.src = currentAudioUrl
      setSpeaking(true)
      void audioRef.current.play().catch(() => setSpeaking(false))
    }
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (isTypingTarget(event.target) || !currentCard) return
      if (event.key === ' ') {
        event.preventDefault()
        setFlipped((value) => !value)
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        handlePrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        handleNext()
      } else if (event.key.toLowerCase() === 's') {
        event.preventDefault()
        toggleStar()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  })

  if (pageState.loading) {
    return (
      <section className={styles.page} aria-labelledby="flashcards-title">
        <h2 id="flashcards-title">Flashcards</h2>
        <p role="status" aria-live="polite">
          Loading flashcards...
        </p>
      </section>
    )
  }

  if (pageState.errorMessage) {
    return (
      <section className={styles.page} aria-labelledby="flashcards-title">
        <h2 id="flashcards-title">Flashcards</h2>
        <div className={styles.stateBlock}>
          <p role="alert">{pageState.errorMessage}</p>
          <Button type="button" variant="secondary" onClick={() => void loadDeck()}>
            Retry
          </Button>
        </div>
      </section>
    )
  }

  if (!pageState.deck) {
    return (
      <section className={styles.page} aria-labelledby="flashcards-title">
        <h2 id="flashcards-title">This deck no longer exists</h2>
        <Link to="/student">Back to student home</Link>
      </section>
    )
  }

  if (pageState.cards.length === 0) {
    return (
      <section className={styles.page} aria-labelledby="flashcards-title">
        <h2 id="flashcards-title">{pageState.deck.title || 'Flashcards'}</h2>
        <div className={styles.stateBlock}>
          <p>This deck has no cards yet.</p>
          <Link to="/student">Back to student home</Link>
        </div>
      </section>
    )
  }

  return (
    <section className={styles.page} aria-labelledby="flashcards-title">
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Flashcards</p>
          <h2 id="flashcards-title">{pageState.deck.title || 'Untitled deck'}</h2>
        </div>
        <div className={styles.headerActions}>
          <Link className={styles.modeLink} to={`/student/decks/${pageState.deck.id}/speedmatch`}>
            Speed Match
          </Link>
          <FlagDeckButton
            deckId={pageState.deck.id}
            alreadyFlagged={uid ? pageState.deck.flaggedBy.includes(uid) : false}
            getIdToken={() => firebaseUser?.getIdToken() ?? Promise.resolve('')}
          />
          <Button type="button" variant="secondary" loading={savingScore} onClick={handleFinish}>
            Finish session
          </Button>
        </div>
      </div>

      {!online ? (
        <div className={styles.offlineNotice} role="status">
          You are offline. This session is saved on this device and your score will sync when
          reconnected.
        </div>
      ) : null}

      <p role="status" aria-live="polite" className="sr-only">
        Card {index + 1} of {visibleCards.length}, {flipped ? 'definition' : 'term'} side.
      </p>

      <div className={styles.toolbar}>
        <p>
          Card {index + 1} of {visibleCards.length}
        </p>
        <label className={styles.starredToggle}>
          <input
            type="checkbox"
            checked={starredOnly}
            disabled={starred.size === 0}
            onChange={(event) => {
              setStarredOnly(event.currentTarget.checked)
              setIndex(0)
              setFlipped(false)
            }}
          />
          Starred only
        </label>
        {starred.size === 0 ? <p className={styles.helper}>Star cards to filter them.</p> : null}
      </div>

      {currentCard ? (
        <div className={styles.studyPanel}>
          <button
            type="button"
            className={styles.flashcard}
            onClick={() => setFlipped((value) => !value)}
          >
            <p className={styles.sideLabel}>{flipped ? 'Definition' : 'Term'}</p>
            <p className={styles.cardText}>{currentText || 'Blank card side'}</p>
          </button>

          {canSpeak ? (
            <Button type="button" variant="secondary" disabled={speaking} onClick={speak}>
              {speaking ? 'Speaking...' : 'Speak'}
            </Button>
          ) : (
            <p className={styles.helper}>Audio unavailable</p>
          )}

          <div className={styles.controls}>
            <Button
              type="button"
              variant="secondary"
              disabled={index === 0}
              onClick={handlePrevious}
              aria-label="Previous card (Left Arrow)"
            >
              Prev
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => setFlipped((value) => !value)}
              aria-label="Flip card (Space)"
            >
              Flip
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleNext}
              aria-label="Next card (Right Arrow)"
            >
              Next
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={toggleStar}
              aria-label="Star card (S)"
            >
              {cardIsStarred ? 'Starred' : 'Star'}
            </Button>
            <Button
              type="button"
              variant={cardIsMissed ? 'danger' : 'secondary'}
              onClick={toggleMissed}
            >
              {cardIsMissed ? 'Marked missed' : 'Mark missed'}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.stateBlock}>
          <p>No starred cards yet.</p>
          <Button type="button" variant="secondary" onClick={() => setStarredOnly(false)}>
            Show all cards
          </Button>
        </div>
      )}

      {scoreError ? (
        <div className={styles.errorBlock} role="alert">
          <p>{scoreError}</p>
          <Button type="button" variant="secondary" loading={savingScore} onClick={handleFinish}>
            Retry save
          </Button>
        </div>
      ) : null}

      <audio
        ref={audioRef}
        aria-hidden="true"
        onEnded={() => setSpeaking(false)}
        onError={() => setSpeaking(false)}
      >
        <track kind="captions" />
      </audio>
    </section>
  )
}
